import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import type { ClientActivity } from '@/features/patients/hooks/useClientActivity'
import type { ClientHistory } from '@/features/patients/hooks/useClientHistory'
import type { TimelineKind } from '@/features/patients/utils/clientTimeline'
import { cn } from '@/lib/utils'
import { TIMELINE_KIND_STYLE } from './ClientTimelineEntry'

export const KIND_LABELS: Record<TimelineKind, string> = {
  call: 'Calls',
  duty: 'Duties',
  visit: 'Visits',
  appointment: 'Appointments',
}

export interface KindState {
  kind: TimelineKind
  /** Unknown, not zero, while the kind is loading or failed to load. */
  count: number | null
  isLoading: boolean
  isError: boolean
}

function kindState(
  kind: TimelineKind,
  slice: { isLoading: boolean; isError: boolean },
  count: number
): KindState {
  const known = !slice.isLoading && !slice.isError
  return { kind, ...slice, count: known ? count : null }
}

/** Which kinds the viewer may see, with each one's count. */
export function timelineKinds(
  activity: ClientActivity,
  history: ClientHistory
): KindState[] {
  const { calls, duties } = activity
  const { visits, appointments } = history
  const states: (KindState | null)[] = [
    calls.allowed
      ? kindState('call', calls, calls.total ?? calls.rows.length)
      : null,
    kindState('duty', duties, duties.rows.length),
    visits.allowed ? kindState('visit', visits, visits.rows.length) : null,
    appointments.allowed
      ? kindState('appointment', appointments, appointments.rows.length)
      : null,
  ]
  return states.filter((state): state is KindState => state !== null)
}

export type TimelineFilter = TimelineKind | 'all'

const CHIP = cn(
  'h-auto min-w-0 gap-1.5 rounded-full border px-2.5 py-0.5 text-[13px] font-normal shadow-none',
  'bg-app border-neutral-subtle-border text-text-body hover:bg-neutral-subtle-bg hover:border-neutral-ui-border hover:text-text-body',
  'data-[state=on]:bg-neutral-high-contrast data-[state=on]:border-neutral-high-contrast data-[state=on]:text-white',
  'focus-visible:ring-primary-solid focus-visible:ring-2 focus-visible:ring-offset-1'
)

function Count({ value }: { value: number | null }) {
  if (value === null) return null
  return (
    <span className="text-neutral-solid tabular-nums group-data-[state=on]/chip:text-white/65">
      {value}
    </span>
  )
}

/**
 * One chip per kind plus "All": a single-choice toggle group, so arrow keys
 * move between chips and assistive tech hears which one is selected.
 */
export function TimelineFilters({
  kinds,
  value,
  onChange,
}: {
  kinds: KindState[]
  value: TimelineFilter
  onChange: (next: TimelineFilter) => void
}) {
  const total = kinds.every((state) => state.count !== null)
    ? kinds.reduce((sum, state) => sum + (state.count ?? 0), 0)
    : null

  return (
    <ToggleGroup
      type="single"
      aria-label="Show"
      spacing={1.5}
      value={value}
      // Choosing the selected chip again would clear the group; keep it.
      onValueChange={(next) => next && onChange(next as TimelineFilter)}
      className="flex-wrap">
      <ToggleGroupItem value="all" className={cn(CHIP, 'group/chip')}>
        All
        <Count value={total} />
      </ToggleGroupItem>
      {kinds.map((state) => (
        <ToggleGroupItem
          key={state.kind}
          value={state.kind}
          className={cn(CHIP, 'group/chip')}>
          <span
            aria-hidden
            className={cn(
              'size-[7px] rounded-full',
              TIMELINE_KIND_STYLE[state.kind].swatch
            )}
          />
          {KIND_LABELS[state.kind]}
          <Count value={state.count} />
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  )
}
